import { useContext, useState } from "react";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@pushpress/pushpress-ui";
import { toast } from "react-toastify";
import { AppContext } from "./../../../../../context/app";
import { defaultToastOptions } from "../../../../../utils/misc";
import {
  buildURLWithEnvironment,
  buildEmailParamsFromTemplate,
} from "../../../helpers";

import { useUserContext } from "../../../hooks/useUserContext";
import { useRevokeUserDocumentMutation } from "../../../../../apis/userApi.hooks";
import { useSendEmail } from "../../../../../apis/messagesApi.hooks";
import { MoreVertical } from "lucide-react";
import { UserDocument } from "src/apis/userApi";
import ConfirmationModal from "../../shared/ConfirmationModal";
import { queryClient } from "../../../../../components/providers/ReactQueryProvider";
type DocumentsProps = {
  document: UserDocument;
  userUuid: string;
};

export default function DocumentsActionMenu({
  userUuid,
  document,
}: DocumentsProps) {
  const {
    client: {
      subdomain,
      company,
      email,
      addressLine1,
      phone,
      websiteUrl,
      uuid: clientUuid,
      id: clientId,
    },
  } = useContext(AppContext);
  const { uuid: documentUuid, is_signed } = document;
  const { userId, userEmail } = useUserContext();
  const { mutate, isLoading: isRevokingDocument } =
    useRevokeUserDocumentMutation();
  const { mutate: sendEmail, isLoading: isSendingEmail } = useSendEmail();
  const [showRevokeDocument, setShowRevokeDocument] = useState<boolean>(false);
  const [showRequestSignature, setShowRequestSignature] =
    useState<boolean>(false);
  const requestSignatureEmail = () => {
    const body = buildEmailParamsFromTemplate({
      template: "user-document-requested",
      clientId,
      clientUuid,
      values: {
        client: {
          company,
          address: addressLine1,
          email,
          phone,
          url: websiteUrl,
        },
        cta_link: buildURLWithEnvironment(
          subdomain,
          `/open/documents/${userUuid}/${documentUuid}`,
        ),
        email_reason: `You're getting this email because you have documents to sign at ${company}`,
      },
      company,
      userUuid,
      userId,
      toAddress: userEmail,
    });
    sendEmail(body, {
      onSuccess: () => {
        onCloseSignature();
        toast.success("Document signature request sent", defaultToastOptions);
      },
      onError: () => {
        toast.error(
          "There was an error requesting the signature",
          defaultToastOptions,
        );
      },
    });
  };

  const revokeDocument = () => {
    mutate(
      { userDocumentId: documentUuid },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["client-user-documents"],
          });
          toast.success("Document revoked", defaultToastOptions);
        },
        onError: () => {
          toast.error(
            "There was an error revoking the document",
            defaultToastOptions
          );
        },
      }
    );
  };
  const onCloseRevoke = () => setShowRevokeDocument(false);
  const onCloseSignature = () => setShowRequestSignature(false);
  return (
    <div className="tw-flex tw-justify-end tw-mr-2">
      <ConfirmationModal
        open={showRevokeDocument}
        onOpenChange={onCloseRevoke}
        title="Revoke user document"
        content="Are you sure you want to revoke this document?"
        onClose={onCloseRevoke}
        onConfirm={revokeDocument}
        confirmationButtonText="Revoke"
        isLoading={isRevokingDocument}
      />
      <ConfirmationModal
        open={showRequestSignature}
        onOpenChange={onCloseSignature}
        title="Email request document signature"
        content="Are you sure you want to email this user to request their signature?"
        onClose={onCloseSignature}
        onConfirm={requestSignatureEmail}
        confirmationButtonText="Request"
        isLoading={isSendingEmail}
      />
      <Menubar asChild>
        <MenubarMenu>
          <MenubarTrigger data-testid={documentUuid}>
            <MoreVertical className="tw-h-5 tw-w-5" />
          </MenubarTrigger>
          <MenubarContent>
            <MenubarItem
              onClick={() =>
                window.open(
                  `/open/documents/${userUuid}/${documentUuid}`,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            >
              {is_signed ? "View document" : "Sign document"}
            </MenubarItem>
            <MenubarItem onClick={() => setShowRevokeDocument(true)}>
              Revoke document
            </MenubarItem>
            {!is_signed ? (
              <MenubarItem onClick={() => setShowRequestSignature(true)}>
                Email member to Request Signature
              </MenubarItem>
            ) : null}
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
    </div>
  );
}
